// NOTE The elements for this module to be inside the Vue instance because they both contain the
// header-search vue component. If this proves problematic then this module may need to be
// refactored to use Vue event handling instead of native.

import throttle from 'lodash/throttle';

let navHeight: number;

export default function init() {
  // ============================== Desktop menu
  const navContainer = document.querySelector<HTMLElement>('.js-nav-container')!;
  const navFixedContainer = document.querySelector<HTMLElement>('.js-nav-fixed-container')!;
  const header = document.querySelector<HTMLElement>('.js-header')!;
  const isHome = header.getAttribute('data-is-homepage') === 'true';

  // Show / open mega menus on hover & enter key
  initMegaNav(navContainer, isHome);

  // Show / hide sticky header on scroll
  initFeatureHeroObserver(navFixedContainer);

  // Add scroll padding when sticky header is visible
  initScrollPadding(navFixedContainer);
}

function toggleStickyHeader(navFixedContainer: HTMLElement, shouldHide: boolean) {
  navFixedContainer.classList.toggle('stuck', !shouldHide);
  navFixedContainer.setAttribute('aria-hidden', shouldHide ? 'true' : 'false');
  navFixedContainer.toggleAttribute('inert', shouldHide);
  document.documentElement.style.setProperty('--nav-height', shouldHide ? `0px` : `${navHeight}px`);
}

function initFeatureHeroObserver(navFixedContainer: HTMLElement) {
  let lastScrollY = window.scrollY;
  let isAboveIntersector = false;
  const headerIntersector = document.querySelector<HTMLElement>('.js-header-intersector')!;

  // Throttled scroll handler with optimized checks
  window.addEventListener(
    'scroll',
    throttle(() => {
      const currentScrollY = window.scrollY;
      if (currentScrollY === lastScrollY) return;

      // Check if we're above the intersector based on scroll position
      const intersectorRect = headerIntersector.getBoundingClientRect();
      isAboveIntersector = intersectorRect.top > 0;

      // Only show header when scrolling up AND we're below the intersector
      const shouldHide = isAboveIntersector || currentScrollY > lastScrollY;
      toggleStickyHeader(navFixedContainer, shouldHide);

      lastScrollY = currentScrollY;
    }, 100),
  );

  // Simple intersection observer
  new IntersectionObserver(
    (entries) => {
      isAboveIntersector = entries[0].boundingClientRect.y > 0;
      toggleStickyHeader(navFixedContainer, true);
    },
    { threshold: 0 },
  ).observe(headerIntersector);
}

function initMegaNav(navContainer: HTMLElement, isHome: boolean) {
  // The container itself is the trigger in this case, not using a seperate element like mobile
  // because of the hover effect which has to be applied to the whole container
  document.querySelectorAll<HTMLElement>('.js-mega-nav').forEach((dropdown) => {
    const trigger = dropdown.querySelector<HTMLAnchorElement>('.js-mega-nav-trigger')!;
    // Show / hide menu on enter key
    trigger.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.keyCode === 13) {
        dropdown.classList.toggle('open');
        trigger.ariaExpanded = dropdown.classList.contains('open') ? 'true' : 'false';
      }
    });

    // Show menu on mouse over
    dropdown.addEventListener('mouseover', () => {
      dropdown.classList.add('open');
      if (isHome) {
        navContainer.classList.remove('bg-transparent');
        navContainer.classList.add('bg-harwood-blue');
      }
      trigger.ariaExpanded = 'true';
    });

    // Hide menu on mouse leave
    dropdown.addEventListener('mouseleave', () => {
      dropdown.classList.remove('open');
      if (isHome) {
        navContainer.classList.remove('bg-harwood-blue');
        navContainer.classList.add('bg-transparent');
      }
      trigger.ariaExpanded = 'false';
    });
  });
}

/**
 * Address problems scrolling to elements which are hidden by the sticky header nav by adding scroll
 * padding. For Formie forms (which scroll to their success message on submit) there's an extra step
 * to stick the header BEFORE it starts scrolling down so that Formie knows the correct position to
 * scroll to.
 */
function initScrollPadding(navFixedContainer: HTMLElement) {
  navHeight = navFixedContainer.clientHeight;

  const initialScrollPadding =
    parseInt(getComputedStyle(document.documentElement).scrollPaddingTop) || 0;
  document.documentElement.style.setProperty(
    '--scroll-padding',
    `calc(${initialScrollPadding}px + var(--nav-height, 0px))`,
  );
  document.documentElement.classList.add('scroll-pt-(--scroll-padding)');

  // Set up resize observer to update nav height
  const resizeObserver = new ResizeObserver((entries) => {
    navHeight = entries[0].borderBoxSize[0].blockSize;
  });
  resizeObserver.observe(navFixedContainer);

  // Monkey patch formie's scrollToForm method to account for sticky nav
  document.addEventListener('onFormieInit', () => {
    document.querySelectorAll<HTMLFormElement>('.fui-form').forEach((form) => {
      // For original see https://github.com/verbb/formie/blob/32ad475fa115f0a1f52c442d1b01621753f29317/src/web/assets/frontend/src/js/formie-form-theme.js#L793
      const scrollToFormOriginal = form.form.formTheme.scrollToForm as (
        ...args: unknown[]
      ) => unknown;
      form.form.formTheme.scrollToForm = function (...args: unknown[]) {
        const isStuck = navFixedContainer.classList.contains('stuck');
        // Checm if already stuck
        if (isStuck) {
          scrollToFormOriginal.call(this, ...args);
          return;
        }

        // Check for scroll-padding-top or `scroll-margin-top`
        const documentStyle = getComputedStyle(document.documentElement);
        const extraPadding = parseInt(documentStyle.scrollPaddingTop) || 0;
        const extraMargin = parseInt(documentStyle.scrollMarginTop) || 0;

        // If scrolling down, stick the nav now
        const top =
          this.$form.parentNode.getBoundingClientRect().top +
          window.pageYOffset -
          100 -
          extraPadding -
          extraMargin;
        if (window.scrollY > top) {
          toggleStickyHeader(navFixedContainer, false);
        }

        scrollToFormOriginal.call(this, ...args);
      };
    });
  });
}
